import { Alert, Box, Stack } from '@mui/material';

import { ConfirmDialog, Typography } from '@occc/ui-design-system';

import { ActionConfirmDialog } from './action-confirm-dialog.component';

export type AdSetPublishValidationErrors = {
  hasAsset: boolean;
  hasOffer: boolean;
  hasKiosks: boolean;
  hasSchedule: boolean | null;
};

export type AdSetPublishDialogProps = {
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  adSetName: string | null;
  validationErrors: AdSetPublishValidationErrors | null;
};

const getValidationMessages = (errors: AdSetPublishValidationErrors): string[] => {
  const messages: string[] = [];

  if (!errors.hasAsset) {
    messages.push('An asset must be selected');
  }
  if (!errors.hasOffer) {
    messages.push('An offer must be selected');
  }
  if (!errors.hasKiosks) {
    messages.push('At least one target kiosk must be specified');
  }
  if (errors.hasSchedule === false) {
    messages.push('A schedule must be set up');
  }
  if (errors.hasSchedule === null) {
    messages.push('Schedule validation requires viewing the full ad set details');
  }

  return messages;
};

export const AdSetPublishDialog = ({
  onClose,
  onConfirm,
  loading,
  adSetName,
  validationErrors,
}: AdSetPublishDialogProps) => {
  const hasValidationErrors =
    validationErrors &&
    (!validationErrors.hasAsset ||
      !validationErrors.hasOffer ||
      !validationErrors.hasKiosks ||
      validationErrors.hasSchedule !== true);

  const validationMessages = validationErrors ? getValidationMessages(validationErrors) : [];

  if (hasValidationErrors) {
    return (
      <ConfirmDialog
        dialogTitle='Cannot Publish Ad Set'
        onClose={onClose}
        open
        maxWidth='sm'
        confirmButtonText='Close'
        confirmButtonProps={{
          onClick: onClose,
        }}
        declineButtonProps={{
          sx: { display: 'none' },
        }}
      >
        <Stack spacing={2}>
          <Typography>
            The Ad Set{adSetName ? ` "${adSetName}"` : ''} cannot be published because the following
            requirements are not met:
          </Typography>
          <Alert severity='error'>
            <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
              {validationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </Box>
          </Alert>
        </Stack>
      </ConfirmDialog>
    );
  }

  const message = `This action will publish Ad Set${adSetName ? ` "${adSetName}"` : ''}.`;

  return (
    <ActionConfirmDialog
      onClose={onClose}
      onConfirm={onConfirm}
      message={message}
      confirmButtonColor='primary'
      confirmButtonDisabled={loading}
    />
  );
};
